import React, { useState } from 'react';
import { Phone, Mail, Globe, Clock, ShieldCheck, CheckCircle2, ArrowRight, Loader2 } from 'lucide-react';
import { companyInfo } from '../data/companyInfo';
import { servicesData } from '../data/servicesData';
import { Logo } from '../components/ui/Logo';
import { sendContactInquiry } from '../services/emailService';

export const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    service: servicesData[0].title,
    budget: '$5,000 - $15,000',
    message: '',
    consent: true,
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName.trim() || !formData.email.trim() || !formData.message.trim()) {
      setErrorMessage('Please fill in your name, email, and project details.');
      return;
    }
    setErrorMessage(null);
    setLoading(true);

    try {
      const res = await sendContactInquiry({
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        service: formData.service,
        budget: formData.budget,
        message: formData.message,
      });

      setLoading(false);
      if (res.success) {
        setSubmitted(true);
      } else {
        setErrorMessage(res.error || 'Failed to dispatch inquiry. Please reach out to info@ebonmedia.com directly.');
      }
    } catch (err: any) {
      setLoading(false);
      setErrorMessage('Submission error. Please email us directly at info@ebonmedia.com');
    }
  };

  return (
    <div className="bg-[#071426] text-[#D5DFEC] py-12 space-y-16">
      
      {/* Page Header */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#0B2342] border border-[#1b2f4a] text-xs font-semibold uppercase tracking-wider text-[#FF851B]">
            <span>GET IN TOUCH</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white font-display tracking-tight leading-tight">
            Let's Build Something Great Together
          </h1>
          <p className="text-base sm:text-lg text-[#D5DFEC] leading-relaxed">
            Have a project scope, system modernization, or enterprise consultation requirement? Reach out to our technology team directly.
          </p>
        </div>
      </section>

      {/* Main Form & Office Info Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {/* Left Column: Direct Contacts & Office Details */}
          <div className="lg:col-span-5 space-y-6">
            <div className="p-8 rounded-2xl bg-[#0c1a2d] border border-[#1b2f4a] shadow-card-dark space-y-6">
              <div className="flex items-center justify-between pb-3 border-b border-[#1b2f4a]">
                <Logo variant="card" className="scale-95 origin-left" />
              </div>
              <h2 className="text-xl font-bold text-white font-display">Contact Details</h2>
              
              <div className="space-y-4 text-xs sm:text-sm">
                <a
                  href={`tel:${companyInfo.contact.phone}`}
                  className="flex items-start gap-3.5 p-3 rounded-xl bg-[#071426] border border-[#1b2f4a] hover:border-[#FF851B] transition-colors group"
                >
                  <Phone size={18} className="text-[#FF851B] shrink-0 mt-0.5" />
                  <div>
                    <span className="block text-[11px] text-[#8FA0B8]">Phone / WhatsApp:</span>
                    <span className="font-bold text-white group-hover:text-[#FF851B] transition-colors">
                      {companyInfo.contact.phoneDisplay}
                    </span>
                  </div>
                </a>

                <a
                  href={`mailto:${companyInfo.contact.email}`}
                  className="flex items-start gap-3.5 p-3 rounded-xl bg-[#071426] border border-[#1b2f4a] hover:border-[#159DE8] transition-colors group"
                >
                  <Mail size={18} className="text-[#159DE8] shrink-0 mt-0.5" />
                  <div>
                    <span className="block text-[11px] text-[#8FA0B8]">Business Inquiries:</span>
                    <span className="font-bold text-white group-hover:text-[#159DE8] transition-colors">
                      {companyInfo.contact.email}
                    </span>
                  </div>
                </a>

                <div className="flex items-start gap-3.5 p-3 rounded-xl bg-[#071426] border border-[#1b2f4a]">
                  <Clock size={18} className="text-[#159DE8] shrink-0 mt-0.5" />
                  <div>
                    <span className="block text-[11px] text-[#8FA0B8]">Business Hours:</span>
                    <span className="font-medium text-white">
                      Monday – Friday: 9:30 AM – 6:30 PM IST (Global time-zone aligned)
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-[#050A13] border border-[#1b2f4a] rounded-xl text-xs text-[#8FA0B8] flex items-start gap-2.5">
                <ShieldCheck size={18} className="text-[#159DE8] shrink-0 mt-0.5" />
                <span>All project communications are protected by NDA. We guarantee prompt 24-hour turnaround for verified inquiries.</span>
              </div>

              {/* Secure Client Payments Options */}
              <div className="p-4 rounded-xl bg-[#071426] border border-[#1b2f4a] space-y-2.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold text-white">Client Payment Options</span>
                  <span className="text-[10px] font-mono text-[#159DE8]">USD • GBP • CAD • INR</span>
                </div>
                <p className="text-[11px] text-[#8FA0B8]">
                  Direct milestone invoicing and retainers supported securely via official PayPal & Stripe checkout.
                </p>
                <div className="flex items-center gap-2.5 pt-1">
                  {/* PayPal - Official Solid Navy & Cyan Icon */}
                  <a
                    href={companyInfo.payments.paypalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Pay with PayPal"
                    className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg bg-[#003087] hover:bg-[#002569] border border-[#004dc5]/40 text-white shadow-sm hover:scale-[1.02] transition-all group"
                  >
                    <svg className="w-4 h-4 fill-[#0079C1]" viewBox="0 0 24 24">
                      <path d="M15.607 4.653H8.941L6.645 19.251H1.82L4.862 0h7.995c3.754 0 6.375 2.294 6.473 5.513-.648-.478-2.105-.86-3.722-.86m6.57 5.546c0 3.41-3.01 6.853-6.958 6.853h-2.493L11.595 24H6.74l1.845-11.538h3.592c4.208 0 7.346-3.634 7.153-6.949a5.24 5.24 0 0 1 2.848 4.686M9.653 5.546h6.408c.907 0 1.942.222 2.363.541-.195 2.741-2.655 5.483-6.441 5.483H8.714Z"/>
                    </svg>
                    <span className="text-xs font-bold text-white tracking-tight">PayPal</span>
                  </a>

                  {/* Stripe - Official Blurple & White Icon */}
                  <a
                    href={companyInfo.payments.stripeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Pay via Stripe"
                    className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg bg-[#635BFF] hover:bg-[#5249e7] border border-[#857dff]/40 text-white shadow-sm hover:scale-[1.02] transition-all group"
                  >
                    <svg className="w-3.5 h-4 fill-white" viewBox="0 0 24 24">
                      <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.594-7.305h.003z"/>
                    </svg>
                    <span className="text-xs font-bold text-white tracking-tight">Stripe</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Global Reach Card */}
            <div className="p-6 rounded-2xl bg-[#050A13] border border-[#1b2f4a] text-xs space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-bold text-white">Global Client Delivery Hub</span>
                <span className="text-[#00C853] font-mono text-[11px]">Worldwide Delivery</span>
              </div>
              <p className="text-[#8FA0B8] leading-relaxed">
                Operating with agile, distributed engineering pods aligned to US, UK, European, and Asia-Pacific time zones for zero-latency project collaboration.
              </p>
            </div>
          </div>

          {/* Right Column: Full Inquiry Form */}
          <div className="lg:col-span-7">
            <div className="p-8 rounded-2xl bg-[#0c1a2d] border border-[#1b2f4a] shadow-card-dark">
              
              {submitted ? (
                <div className="py-12 text-center space-y-4">
                  <div className="w-16 h-16 mx-auto bg-green-500/20 text-[#00C853] rounded-full flex items-center justify-center border border-green-500/40">
                    <CheckCircle2 size={36} />
                  </div>
                  <h2 className="text-2xl font-bold text-white font-display">Inquiry Successfully Dispatched!</h2>
                  <p className="text-sm text-[#D5DFEC] max-w-md mx-auto leading-relaxed">
                    Thank you, <strong className="text-white">{formData.fullName}</strong>. Our director and technical lead will inspect your specifications and email you back at <strong className="text-[#159DE8]">{formData.email}</strong> within 24 business hours.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({
                        fullName: '',
                        email: '',
                        phone: '',
                        company: '',
                        service: servicesData[0].title,
                        budget: '$5,000 - $15,000',
                        message: '',
                        consent: true,
                      });
                    }}
                    className="mt-4 px-6 py-2.5 rounded-full bg-[#050A13] text-xs font-semibold text-white hover:bg-[#071426] border border-[#1b2f4a] transition-colors"
                  >
                    Submit Another Inquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="border-b border-[#1b2f4a] pb-3 mb-2 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-white font-display">
                      Submit Your Project Inquiry
                    </h2>
                    <span className="text-[11px] text-[#8FA0B8]">* Required</span>
                  </div>

                  {errorMessage && (
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs">
                      {errorMessage}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Full Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.fullName}
                        onChange={e => setFormData({ ...formData, fullName: e.target.value })}
                        placeholder="e.g. Michael Chen"
                        className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/40 focus:outline-none focus:border-[#159DE8] text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Business Email *</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                        placeholder="m.chen@enterprise.com"
                        className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/40 focus:outline-none focus:border-[#159DE8] text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Phone Number</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={e => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+91 00000 00000"
                        className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/40 focus:outline-none focus:border-[#159DE8] text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Company Name (Optional)</label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={e => setFormData({ ...formData, company: e.target.value })}
                        placeholder="Your company / brand"
                        className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/40 focus:outline-none focus:border-[#159DE8] text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Service Required *</label>
                      <select
                        value={formData.service}
                        onChange={e => setFormData({ ...formData, service: e.target.value })}
                        className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white focus:outline-none focus:border-[#159DE8] text-sm"
                      >
                        {servicesData.map(s => (
                          <option key={s.id} value={s.title}>{s.title}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Estimated Budget (Optional)</label>
                      <select
                        value={formData.budget}
                        onChange={e => setFormData({ ...formData, budget: e.target.value })}
                        className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white focus:outline-none focus:border-[#159DE8] text-sm"
                      >
                        <option value="Under $5,000">Under $5,000</option>
                        <option value="$5,000 - $15,000">$5,000 - $15,000</option>
                        <option value="$15,000 - $40,000">$15,000 - $40,000</option>
                        <option value="$40,000+">$40,000+ (Enterprise)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Project Details & Objectives *</label>
                    <textarea
                      required
                      rows={4}
                      value={formData.message}
                      onChange={e => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Outline your requirements, technology stack preferences, current bottlenecks, or target go-live date..."
                      className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/40 focus:outline-none focus:border-[#159DE8] text-sm resize-none"
                    />
                  </div>

                  <div className="flex items-center gap-2 pt-1 text-xs text-[#8FA0B8]">
                    <input
                      type="checkbox"
                      id="contact-consent"
                      checked={formData.consent}
                      onChange={e => setFormData({ ...formData, consent: e.target.checked })}
                      className="rounded border-[#1b2f4a] text-[#FF851B] focus:ring-[#FF851B]"
                    />
                    <label htmlFor="contact-consent" className="cursor-pointer">
                      I agree to receive communications from EBON Media Solutions regarding my inquiry.
                    </label>
                  </div>

                  <div className="pt-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white font-bold text-sm hover:shadow-glow-orange hover:scale-[1.01] active:scale-[0.99] transition-all flex items-center justify-center gap-2 disabled:opacity-60 shadow-lg"
                    >
                      {loading ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          <span>Submitting Inquiry...</span>
                        </>
                      ) : (
                        <>
                          <span>Submit Project Inquiry</span>
                          <ArrowRight size={16} />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}

            </div>
          </div>

        </div>
      </section>

    </div>
  );
};
